import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";

import { Button, Card, Icon, Popconfirm, Spin, Statistic, Table, Tabs, Tag, Upload, message } from "antd";

import {
    _deleteTermContract,
    _deleteTermContractAttachment,
    _getAdmins,
    _getEnterpriseData,
    _getTermContract,
    _getTermContractAttachment,
    _postTermContractAttachment
} from "../../services";
import TermContractActivity from "./activity";
import TermContractHeaderModal, { EndDateModal } from "./header-modal";
import TermContractLines, { LinesActions } from "./lines";
import { contractBadge, contractStatus, errorMessage, formatPct, formatPeriod, saveBlob, toDateTime } from "./helpers";

const ATTACHMENT_ACCEPT = ".pdf,.eml,.msg,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls";

class TermContractAttachments extends React.Component {

    state = { uploading: false };

    upload = async ({
        file,
    }) => {
        const { contract, onChange } = this.props;
        this.setState({ uploading: true });
        try {
            await _postTermContractAttachment({ id: contract._id, file });
            message.success(`${file.name} attached`);
            onChange();
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ uploading: false });
        }
    };

    download = async ({
        attachment,
    }) => {
        try {
            const { data } = await _getTermContractAttachment({ id: this.props.contract._id, attachmentId: attachment._id });
            saveBlob({ blob: data, fileName: attachment.name });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    remove = async ({
        attachment,
    }) => {
        const { contract, onChange } = this.props;
        try {
            await _deleteTermContractAttachment({ id: contract._id, attachmentId: attachment._id });
            message.success(`${attachment.name} removed`);
            onChange();
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    render() {
        const { contract, readOnly, adminName } = this.props;
        return (
            <div>
                {!readOnly && (
                    <div style={{ marginBottom: 12 }}>
                        {/* false: we post the file ourselves */}
                        <Upload accept={ATTACHMENT_ACCEPT} showUploadList={false} beforeUpload={file => { this.upload({ file }); return false; }}>
                            <Button loading={this.state.uploading}><Icon type="upload" /> Attach file</Button>
                        </Upload>
                        <span style={{ marginLeft: 8, color: "rgba(0,0,0,0.45)" }}>PDF, email (.eml/.msg), image or Excel; 25 MB max</span>
                    </div>
                )}
                <Table
                    rowKey="_id"
                    size="small"
                    pagination={false}
                    dataSource={contract.attachments || []}
                    columns={[{
                        title: "File",
                        dataIndex: "name",
                        render: (name, attachment) => <a onClick={() => this.download({ attachment })}>{name}</a>
                    }, {
                        title: "Size",
                        dataIndex: "size",
                        render: size => size ? `${Math.ceil(size / 1024)} KB` : "-"
                    }, {
                        title: "Uploaded",
                        key: "uploaded",
                        render: (_, attachment) => `${toDateTime({ date: attachment.uploadedAt })} by ${adminName({ userId: attachment.uploadedBy })}`
                    }, ...(readOnly ? [] : [{
                        title: "",
                        key: "actions",
                        width: 100,
                        render: (_, attachment) => (
                            <Popconfirm title={`Remove ${attachment.name}?`} onConfirm={() => this.remove({ attachment })}>
                                <Button size="small" type="danger">Remove</Button>
                            </Popconfirm>
                        )
                    }])]}
                />
            </div>
        );
    }
}


const AMBER = "#ad4e00";

/** Lines, suppliers, margin and flags over all live lines (the lines list's contract-wide `summary`). */
const SummaryStrip = ({ summary }) => {
    const flagged = Object.values(summary.flagCounts || {}).reduce((sum, n) => sum + n, 0);
    /* ponytail: "Needs attention" counts flags, not lines (a line with two flags counts twice); `summary` has no distinct-line count. */
    const tiles = [
        { title: "Lines", value: summary.lines.toLocaleString() },
        { title: "Listing suppliers", value: summary.listingSuppliers.toLocaleString() },
        { title: "Backing suppliers", value: summary.backingSellers.toLocaleString() },
        { title: "Avg margin", value: formatPct({ value: summary.avgMarginPct }) },
        { title: "Needs attention", value: `${flagged.toLocaleString()} flag${flagged === 1 ? "" : "s"}`, amber: flagged > 0 }
    ];
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, marginTop: 16 }}>
            {tiles.map(tile => (
                <Card size="small" key={tile.title}>
                    <Statistic title={tile.title} value={tile.value} valueStyle={tile.amber ? { color: AMBER } : undefined} />
                </Card>
            ))}
        </div>
    );
};

class TermContractProfile extends React.Component {

    state = { contract: null, enterpriseName: null, admins: {}, summary: null, editing: null, activityKey: 0, activeTab: "lines" };

    lines = React.createRef();

    componentDidMount() {
        this.load();
        _getAdmins()
            .then(res => this.setState({
                admins: (res.data.users || []).reduce((names, admin) => ({
                    ...names,
                    [admin._id]: (admin.details && admin.details.name) || (admin.local && admin.local.email) || admin._id
                }), {})
            }))
            .catch(e => console.error("Failed to load admins", e));
    }

    load = async () => {
        try {
            const { data } = await _getTermContract({ id: this.props.termContractId });
            const contract = data.termContract;
            this.setState({ contract });
            const enterprise = await _getEnterpriseData(contract.enterpriseId);
            this.setState({ enterpriseName: enterprise.data.enterprise ? enterprise.data.enterprise.companyName : `${contract.enterpriseId}` });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    remove = async () => {
        try {
            await _deleteTermContract({ id: this.state.contract._id });
            message.success("Term contract deleted");
            this.props.history.push("/enterprise/term-contracts");
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    adminName = ({
        userId,
    }) => this.state.admins[userId] || userId || "-";

    onSaved = () => {
        this.setState({ editing: null });
        this.load();
    };

    renderHeader({
        readOnly,
    }) {
        const { contract, enterpriseName, summary } = this.state;
        const badge = contractBadge({ contract });
        return (
            <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12 }}><Link to="/enterprise/term-contracts">Term contracts</Link> / {contract.reference}</div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0 }}>{contract.reference} <Tag color={badge.color}>{badge.text}</Tag></h2>
                        <div>
                            {contract.title ? `${contract.title} · ` : ""}<b>{enterpriseName || `${contract.enterpriseId}`}</b> · {formatPeriod({ contract })} (SGT)
                        </div>
                        {contract.notes && <div style={{ color: "rgba(0,0,0,0.45)", marginTop: 4 }}>{contract.notes}</div>}
                    </div>
                    {!readOnly && <Button icon="edit" onClick={() => this.setState({ editing: "details" })}>Edit details</Button>}
                    {!readOnly && <Button icon="calendar" onClick={() => this.setState({ editing: "endDate" })}>Extend / end early</Button>}
                    {!readOnly && (
                        <Popconfirm title="Delete this term contract? It stops applying immediately." onConfirm={this.remove}>
                            <Button type="danger" icon="delete">Delete</Button>
                        </Popconfirm>
                    )}
                </div>
                {summary && <SummaryStrip summary={summary} />}
            </Card>
        );
    }

    render() {
        const { contract, enterpriseName, admins, summary, editing, activityKey, activeTab } = this.state;
        if (!contract) return <Card><Spin /></Card>;

        const readOnly = contractStatus({ contract }) === "Deleted";
        return (
            <div>
                {this.renderHeader({ readOnly })}
                <Card>
                    {/* opening Activity remounts it, so an edit made on another tab shows up */}
                    <Tabs
                        activeKey={activeTab}
                        onChange={key => this.setState(state => ({
                            activeTab: key,
                            activityKey: key === "activity" ? state.activityKey + 1 : state.activityKey
                        }))}
                        tabBarExtraContent={activeTab === "lines" ? (
                            <LinesActions
                                contract={contract}
                                enterpriseName={enterpriseName}
                                readOnly={readOnly}
                                onAdded={() => this.lines.current.reload()}
                                onUploaded={() => this.lines.current.reloadAfterUpload()}
                            />
                        ) : null}
                    >
                        <Tabs.TabPane tab={`Lines${summary ? ` (${summary.lines.toLocaleString()})` : ""}`} key="lines">
                            <TermContractLines
                                ref={this.lines}
                                contract={contract}
                                enterpriseName={enterpriseName}
                                readOnly={readOnly}
                                onSummary={({ summary }) => this.setState({ summary })}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={`Documents (${(contract.attachments || []).length})`} key="documents">
                            <TermContractAttachments contract={contract} readOnly={readOnly} adminName={this.adminName} onChange={this.load} />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Activity" key="activity">
                            <TermContractActivity key={activityKey} contractId={contract._id} admins={admins} adminName={this.adminName} />
                        </Tabs.TabPane>
                    </Tabs>
                </Card>

                {editing === "details" && (
                    <TermContractHeaderModal
                        contract={contract}
                        enterpriseName={enterpriseName}
                        onCancel={() => this.setState({ editing: null })}
                        onSaved={this.onSaved}
                    />
                )}
                {editing === "endDate" && (
                    <EndDateModal contract={contract} onCancel={() => this.setState({ editing: null })} onSaved={this.onSaved} />
                )}
            </div>
        );
    }
}

export default withRouter(TermContractProfile);



// WEBPACK FOOTER //
// ./src/components/term-contract/profile.js